import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, AlertTitle, Button, Collapse, Stack } from '@mui/material';

import { UserDisplayPreferences, useDisplayPreferences, usePostDisplayPreferences } from '@/api/userAccount';
import { useDisclosure } from '@/hooks/useDisclosure';

// suppressing banner as of July 17, 2026 since we're no longer trumpeting v2 and we don't have anything in particular
// to say to new users. leaving the code in place so we can quickly resurrect our old design if we have anything to announce;
// just delete this constant if you want to bring it back (along with updating ANNOUNCEMENT).
const NO_ANNOUNCEMENT = true;

// timestamp is yyyymmddhhmm. Users see a new announcement if the last one they saw had a lower timestamp than the one we're currently offering.
const ANNOUNCEMENT = {
    timestamp: 202602160000,
    message: (
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Stack>
                <AlertTitle>
                    <span>Welcome to the Spotter Dashboard</span>
                </AlertTitle>

                <span>
                    Enjoy new data visualizations, faster performance, clearer device status, and new tools for sharing
                    specific data moments &mdash; without any changes to your device behavior.
                </span>
            </Stack>

            <Button
                variant="outlined"
                size="small"
                sx={{ color: 'white', borderColor: 'white' }}
                target="_blank"
                href="https://www.sofarocean.com/posts/spotter-dashboard-updates-2026"
            >
                Learn more <ArrowForwardIcon sx={{ pl: 1 }} />
            </Button>
        </Stack>
    ),
} as const;

export const Announcement = () => {
    const { data: displayPreferences, isPending } = useDisplayPreferences();

    if (isPending || !displayPreferences) {
        return false;
    }

    if (NO_ANNOUNCEMENT) {
        return false;
    }

    return <Banner displayPreferences={displayPreferences} />;
};

const Banner = ({ displayPreferences }: { displayPreferences: UserDisplayPreferences }) => {
    const { mutateAsync: postDisplayPreferences } = usePostDisplayPreferences();

    const { timestamp, message } = ANNOUNCEMENT;

    const hasSubmitedFeedback = sessionStorage.getItem('feedbackSubmitted') === 'true';
    const dismissedLatestAnnouncement =
        !!displayPreferences.dismissedAnnouncement && displayPreferences.dismissedAnnouncement >= timestamp;

    const { close: hideAnnoucement, isOpen: isAnnouncementOpen } = useDisclosure(
        !(hasSubmitedFeedback || dismissedLatestAnnouncement)
    );

    if (!timestamp) {
        return;
    }

    const closeAnnouncement = () => {
        hideAnnoucement();
        postDisplayPreferences({ ...displayPreferences, dismissedAnnouncement: timestamp });
    };

    return (
        <>
            <Collapse
                /* Not too happy about this, but the stock min-height:0 inserted directly as a style attr causes the map viewport to overlay the announcement. Maybe
                need to investigate alternative banners? */
                sx={{ minHeight: 'initial !important' }}
                onTransitionEnd={() => window.dispatchEvent(new Event('resize'))}
                in={isAnnouncementOpen}
            >
                <Alert
                    severity="info"
                    variant="filled"
                    onClose={closeAnnouncement}
                    sx={{
                        borderRadius: '0',
                        '.MuiAlert-message': { width: '100%' },
                    }}
                >
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                        {message}
                    </Stack>
                </Alert>
            </Collapse>
        </>
    );
};
